// Dropbox's Chooser (ART-369) — Dropbox's own file picker, loaded from dropbox.com and opened in a popup. It is how
// a person picks the files to import: they sign in to Dropbox inside it, and we get back each file's id, name, size
// and a direct download link good for four hours. No account is connected to us and we hold no credential.
//
// Two browser rules shape this file. The Chooser opens a POPUP, so `Dropbox.choose` must run inside the click that
// asked for it — the script is therefore loaded when the dialog opens, not when the button is pressed. And the
// Chooser refuses to open on a host that is not listed under the app's "Chooser / Saver / Embedder domains" in the
// Dropbox console; it says "App is misconfigured" when that is the cause.

import { useEffect, useState } from "react";

/** One file the Chooser returned. `link` is the direct link (`linkType: "direct"`). */
export interface ChooserFile {
  id: string;
  name: string;
  link: string;
  bytes: number;
  isDir?: boolean;
}

interface ChooseOptions {
  linkType: "direct";
  multiselect: boolean;
  sizeLimit?: number;
  folderselect: false;
  success: (files: ChooserFile[]) => void;
  cancel: () => void;
}

interface DropboxDropins {
  choose: (options: ChooseOptions) => void;
  isBrowserSupported: () => boolean;
}

declare global {
  interface Window {
    Dropbox?: DropboxDropins;
  }
}

const SCRIPT_ID = "dropboxjs";
// eslint-disable-next-line no-restricted-syntax -- Dropbox's own picker script, not an app base
const SCRIPT_SRC = "https://www.dropbox.com/static/api/2/dropins.js";

let loading: Promise<DropboxDropins> | null = null;

/** Load `dropins.js` once, with the lane's app key on the tag (where the Chooser reads it). */
export function loadDropboxChooser(appKey: string): Promise<DropboxDropins> {
  if (window.Dropbox) return Promise.resolve(window.Dropbox);
  if (loading) return loading;
  loading = new Promise<DropboxDropins>((resolve, reject) => {
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.dataset.appKey = appKey;
    script.async = true;
    script.onload = () => (window.Dropbox ? resolve(window.Dropbox) : reject(new Error("Dropbox's picker didn't load.")));
    script.onerror = () => {
      script.remove();
      reject(new Error("Dropbox's picker didn't load."));
    };
    document.head.appendChild(script);
  });
  loading.catch(() => {
    loading = null;   // a failed load (offline, blocked) can be tried again by reopening the dialog
  });
  return loading;
}

export type ChooserState = "loading" | "ready" | "unsupported" | "failed";

/**
 * The Chooser for this dialog: loads it as soon as there is an app key, and `choose` opens it. `choose` must be
 * called from a click handler — it opens a popup.
 */
export function useDropboxChooser(appKey: string | null | undefined) {
  const [state, setState] = useState<ChooserState>(appKey ? "loading" : "failed");

  useEffect(() => {
    if (!appKey) return;
    let alive = true;
    loadDropboxChooser(appKey).then(
      (dropbox) => alive && setState(dropbox.isBrowserSupported() ? "ready" : "unsupported"),
      () => alive && setState("failed"),
    );
    return () => {
      alive = false;
    };
  }, [appKey]);

  const choose = (sizeLimit: number | undefined, onPicked: (files: ChooserFile[]) => void) => {
    window.Dropbox?.choose({
      linkType: "direct",
      multiselect: true,
      // Folders cannot be picked with direct links (Dropbox's own rule); the person opens the folder and selects.
      folderselect: false,
      sizeLimit,
      success: (files) => onPicked(files.filter((f) => !f.isDir)),
      cancel: () => {},
    });
  };

  return { state, choose };
}
